import { useEffect, useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import useBreakpoint from '../hooks/useBreakpoint';

const T = {
  ink:    '#111',
  muted:  '#888',
  border: '#e5e5e5',
  bg:     '#f9fafb',
};

const S = {
  paid:    { label: 'Bezahlt',    color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0' },
  pending: { label: 'Offen',      color: '#b45309', bg: '#fffbeb', border: '#fde68a' },
  partial: { label: 'Teilweise',  color: '#111', bg: '#f5f5f5', border: T.border },
  overdue: { label: 'Überfällig', color: '#dc2626', bg: '#fff1f2', border: '#fecaca' },
};

const fmtEur = v =>
  (!v && v !== 0) ? '–'
  : Number(v).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';

const fmt = d =>
  d ? new Date(d).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '–';

const fmtShort = d =>
  d ? new Date(d).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' }) : '–';

function currentYear() { return new Date().getFullYear(); }

function yearStart(y) { return `${y}-01-01`; }
function yearEnd(y)   { return `${y}-12-31`; }

function leaseStatus(start, end) {
  if (!start) return null;
  const now = new Date(); now.setHours(0,0,0,0);
  const s = new Date(start);
  if (s > now) return { label: 'Zukünftig', color: '#888', bg: '#f7f7f7', border: '#e5e5e5' };
  if (end && new Date(end) < now) return { label: 'Beendet', color: '#888', bg: '#f3f4f6', border: '#d1d5db' };
  return { label: 'Aktiv', color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0' };
}

function Badge({ cfg }) {
  if (!cfg) return null;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      fontSize: 12, fontWeight: 600, padding: '3px 10px', borderRadius: 99,
      background: cfg.bg, border: `1.5px solid ${cfg.border}`, color: cfg.color,
      whiteSpace: 'nowrap',
    }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: cfg.color, flexShrink: 0 }} />
      {cfg.label}
    </span>
  );
}

/* ── Filter chip with X ── */
function FilterChip({ label, value, onRemove }) {
  return (
    <div style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '5px 10px 5px 12px', borderRadius: 6,
      border: `1px solid ${T.border}`, background: 'white',
      fontSize: 13, color: T.ink, whiteSpace: 'nowrap', cursor: 'default',
    }}>
      <span style={{ color: T.muted, fontSize: 12 }}>{label}:</span>
      <span style={{ fontWeight: 500 }}>{value}</span>
      <button
        onClick={onRemove}
        style={{
          border: 'none', background: 'none', cursor: 'pointer',
          padding: '1px 2px', color: T.muted, display: 'flex', alignItems: 'center',
          borderRadius: 4, lineHeight: 1,
        }}
      >
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
          <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
        </svg>
      </button>
    </div>
  );
}

/* ── Dropdown wrapper ── */
function Dropdown({ label, open, onToggle, children, width = 220 }) {
  return (
    <div style={{ position: 'relative' }}>
      <button
        onClick={onToggle}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '7px 12px', borderRadius: 6, border: `1px solid ${T.border}`,
          background: open ? T.bg : 'white', cursor: 'pointer',
          fontSize: 13, color: T.ink, fontWeight: 500,
        }}
      >
        {label}
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
          <polyline points="6 9 12 15 18 9"/>
        </svg>
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, marginTop: 4,
          background: 'white', border: `1px solid ${T.border}`, borderRadius: 8,
          boxShadow: '0 4px 16px rgba(0,0,0,0.10)', zIndex: 50, minWidth: width,
          padding: 8,
        }}>
          {children}
        </div>
      )}
    </div>
  );
}

function DropdownItem({ label, selected, onClick }) {
  return (
    <div
      onClick={onClick}
      style={{
        padding: '8px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 13,
        background: selected ? '#f5f5f5' : 'transparent',
        color: selected ? T.ink : T.ink,
        fontWeight: selected ? 600 : 400,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}
      onMouseEnter={e => { if (!selected) e.currentTarget.style.background = T.bg; }}
      onMouseLeave={e => { if (!selected) e.currentTarget.style.background = 'transparent'; }}
    >
      {label}
      {selected && (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.ink} strokeWidth="2.5" strokeLinecap="round">
          <polyline points="20 6 9 17 4 12"/>
        </svg>
      )}
    </div>
  );
}

/* ── Empty state ── */
function EmptyState({ filtered, onReset }) {
  if (filtered) {
    return (
      <div style={{ background:'white', border:`1px solid ${T.border}`, borderRadius:14, textAlign:'center', padding:'60px 24px' }}>
        <div style={{ width:56,height:56,background:'#f7f7f7',borderRadius:14,display:'flex',alignItems:'center',justifyContent:'center',margin:'0 auto 16px' }}>
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke={T.muted} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
        </div>
        <div style={{ fontSize:16,fontWeight:700,color:T.ink,marginBottom:8 }}>Keine Ergebnisse gefunden</div>
        <div style={{ fontSize:13,color:T.muted,lineHeight:1.7,marginBottom:20 }}>Ändere die Filter oder setze sie zurück.</div>
        <button onClick={onReset} className="btn-secondary">Filter zurücksetzen</button>
      </div>
    );
  }
  return (
    <div style={{ background:'white', border:`1px solid ${T.border}`, borderRadius:14, overflow:'hidden' }}>
      <div style={{ padding:'48px 40px' }}>
        <div style={{ display:'flex',alignItems:'flex-start',gap:32,marginBottom:40 }}>
          <div style={{ flexShrink:0,width:64,height:64,background:'#f7f7f7',borderRadius:16,display:'flex',alignItems:'center',justifyContent:'center',border:'1.5px solid e5e5e5' }}>
            <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke={T.ink} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg>
          </div>
          <div style={{ flex:1 }}>
            <div style={{ fontSize:20,fontWeight:700,color:T.ink,marginBottom:8,letterSpacing:'-0.3px' }}>Noch keine Mietverhältnisse vorhanden</div>
            <div style={{ fontSize:14,color:T.muted,lineHeight:1.8,maxWidth:520 }}>Lege zunächst Mieter mit einem aktiven Mietvertrag an. Die Mietübersicht zeigt dir alle laufenden Mietverhältnisse, Zahlungsstatus und Vertragsdaten auf einen Blick.</div>
            <a href="/tenants" className="btn-primary" style={{ marginTop:20,display:'inline-flex',alignItems:'center',textDecoration:'none' }}>Zu den Mietern →</a>
          </div>
        </div>
        <div style={{ display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:16 }}>
          {[
            { icon:<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={T.ink} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>, title:'Vertragslaufzeiten', text:'Sieh auf einen Blick, wann Mietverträge enden oder verlängert werden.' },
            { icon:<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={T.ink} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>, title:'Zahlungsstatus', text:'Welche Mieter zahlen pünktlich — alle Daten auf einen Blick.' },
            { icon:<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={T.ink} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>, title:'Pro Objekt', text:'Filtere nach Immobilien und erhalte eine strukturierte Übersicht.' },
          ].map((f,i)=>(
            <div key={i} style={{ background:'#f7f7f7',borderRadius:12,padding:'18px 20px',border:`1px solid ${T.border}` }}>
              <div style={{ width:36,height:36,background:'white',borderRadius:9,display:'flex',alignItems:'center',justifyContent:'center',marginBottom:12,border:`1px solid ${T.border}`,boxShadow:'0 1px 4px rgba(0,0,0,0.05)' }}>{f.icon}</div>
              <div style={{ fontSize:13,fontWeight:700,color:T.ink,marginBottom:4 }}>{f.title}</div>
              <div style={{ fontSize:12,color:T.muted,lineHeight:1.6 }}>{f.text}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ── Table row ── */
function Row({ tenant, last, onEnd, onEdit, onDelete, isMobile }) {
  const ls = leaseStatus(tenant.lease_start, tenant.lease_end);
  const isActive = ls?.label === 'Aktiv';
  const propName = tenant.properties?.name || '–';
  const propCity = tenant.properties?.city || '';

  if (isMobile) {
    return (
      <div style={{
        background: 'white', borderRadius: 10, border: `1px solid ${T.border}`,
        padding: '16px', marginBottom: 10,
      }}>
        {/* Name + Status */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: T.ink }}>
              {tenant.first_name} {tenant.last_name}
            </div>
            {tenant.email && (
              <div style={{ fontSize: 12, color: T.muted, marginTop: 2 }}>{tenant.email}</div>
            )}
          </div>
          <Badge cfg={ls} />
        </div>
        {/* Objekt */}
        <div style={{ fontSize: 13, color: T.muted, marginBottom: 8 }}>
          {propName}{propCity ? `, ${propCity}` : ''}
        </div>
        {/* Miete */}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
          <span style={{ fontSize: 12, color: T.muted }}>Gesamtmiete</span>
          <span style={{ fontSize: 14, fontWeight: 700, color: T.ink }}>
            {fmtEur((Number(tenant.monthly_rent) || 0) + (Number(tenant.advance_payment) || 0))}
          </span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
          <span style={{ fontSize: 12, color: T.muted }}>Kaltmiete / NK</span>
          <span style={{ fontSize: 13, color: T.muted }}>
            {fmtEur(tenant.monthly_rent)} / {fmtEur(tenant.advance_payment)}
          </span>
        </div>
        {/* Aktionen */}
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => onEdit(tenant)} className="btn-ghost" style={{ fontSize: 12, height: 32, padding: '0 12px', flex: 1 }}>Bearb.</button>
          {isActive && (
            <button onClick={() => onEnd(tenant)} style={{ fontSize: 12, height: 32, padding: '0 12px', background: 'none', border: `1px solid ${T.border}`, borderRadius: 8, color: '#b45309', cursor: 'pointer', fontWeight: 600, whiteSpace: 'nowrap', flex: 1 }}>
              Beenden
            </button>
          )}
          <button onClick={() => onDelete(tenant)} style={{ fontSize: 12, height: 32, padding: '0 12px', background: 'none', border: `1px solid ${T.border}`, borderRadius: 8, color: '#dc2626', cursor: 'pointer', fontWeight: 600, flex: 1 }}>
            Löschen
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: '1.6fr 1.4fr 1.1fr 110px 110px 110px 100px auto',
      alignItems: 'center', padding: '13px 20px',
      borderBottom: last ? 'none' : `1px solid ${T.border}`,
      background: 'white',
      transition: 'background 0.1s',
    }}
    onMouseEnter={e => e.currentTarget.style.background = T.bg}
    onMouseLeave={e => e.currentTarget.style.background = 'white'}
    >
      {/* Mieter */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <span style={{ fontSize: 14, fontWeight: 600, color: T.ink }}>
          {tenant.first_name} {tenant.last_name}
        </span>
        {tenant.email && (
          <span style={{ fontSize: 12, color: T.muted }}>{tenant.email}</span>
        )}
      </div>
      {/* Objekt */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <span style={{ fontSize: 14, color: T.ink }}>{propName}</span>
        {propCity && <span style={{ fontSize: 12, color: T.muted }}>{propCity}</span>}
      </div>
      {/* Mietzeitraum */}
      <div style={{ fontSize: 13, color: T.muted }}>
        {fmtShort(tenant.lease_start)}{tenant.lease_end ? ` – ${fmtShort(tenant.lease_end)}` : ' – unbefristet'}
      </div>
      {/* Kaltmiete */}
      <div style={{ fontSize: 14, color: T.ink, textAlign: 'right', paddingRight: 8 }}>
        {fmtEur(tenant.monthly_rent)}
      </div>
      {/* NK-Vorauszahlung */}
      <div style={{ fontSize: 14, color: T.ink, textAlign: 'right', paddingRight: 8 }}>
        {fmtEur(tenant.advance_payment)}
      </div>
      {/* Gesamtmiete */}
      <div style={{ fontSize: 14, fontWeight: 600, color: T.ink, textAlign: 'right', paddingRight: 8 }}>
        {fmtEur((Number(tenant.monthly_rent) || 0) + (Number(tenant.advance_payment) || 0))}
      </div>
      {/* Status */}
      <div>
        <Badge cfg={ls} />
      </div>
      {/* Aktionen */}
      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
        <button onClick={() => onEdit(tenant)} className="btn-ghost" style={{ fontSize: 11, height: 30, padding: '0 10px' }}>Bearb.</button>
        {isActive && (
          <button onClick={() => onEnd(tenant)} style={{ fontSize: 11, height: 30, padding: '0 10px', background: 'none', border: `1px solid ${T.border}`, borderRadius: 8, color: '#b45309', cursor: 'pointer', fontWeight: 600, whiteSpace: 'nowrap' }}>
            Beenden
          </button>
        )}
        <button onClick={() => onDelete(tenant)} style={{ fontSize: 11, height: 30, padding: '0 10px', background: 'none', border: `1px solid ${T.border}`, borderRadius: 8, color: '#dc2626', cursor: 'pointer', fontWeight: 600 }}>
          Löschen
        </button>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════
   Main page
══════════════════════════════════════ */
export default function Mietuebersicht() {
  const { user } = useAuth();
  const { addToast } = useToast();
  const { isMobile } = useBreakpoint();

  const [tenants,    setTenants]    = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading,    setLoading]    = useState(true);

  /* filters */
  const [search,       setSearch]       = useState('');
  const [filterStatus, setFilterStatus] = useState('');   // 'Aktiv'|'Beendet'|'Zukünftig'|''
  const [filterProp,   setFilterProp]   = useState('');   // property id
  const [yearFrom,     setYearFrom]     = useState(currentYear());
  const [yearTo,       setYearTo]       = useState(currentYear());
  const [customRange,  setCustomRange]  = useState(false);
  const [customStart,  setCustomStart]  = useState('');
  const [customEnd,    setCustomEnd]    = useState('');

  /* dropdown open state */
  const [openStatus, setOpenStatus] = useState(false);
  const [openProp,   setOpenProp]   = useState(false);
  const [openTime,   setOpenTime]   = useState(false);
  const [openFilter, setOpenFilter] = useState(false);

  /* close dropdowns on outside click */
  useEffect(() => {
    const close = () => { setOpenStatus(false); setOpenProp(false); setOpenTime(false); setOpenFilter(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const load = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    const { data: tData } = await supabase
      .from('tenants')
      .select('*, properties(id, name, city, street, zip)')
      .eq('owner_id', user.id)
      .order('created_at', { ascending: false });
    const { data: pData } = await supabase
      .from('properties')
      .select('id, name, city')
      .eq('owner_id', user.id)
      .order('name');
    setTenants(tData || []);
    setProperties(pData || []);
    setLoading(false);
  }, [user]);

  useEffect(() => { load(); }, [load]);

  const [editTenant, setEditTenant] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  async function handleEnd(tenant) {
    if (!window.confirm(`Mietverhältnis von ${tenant.first_name} ${tenant.last_name} wirklich beenden?`)) return;
    const today = new Date().toISOString().split('T')[0];
    const { error } = await supabase.from('tenants').update({ lease_end: today }).eq('id', tenant.id).eq('owner_id', user.id);
    if (error) { addToast('Fehler: ' + error.message, 'error'); return; }
    setTenants(prev => prev.map(t => t.id === tenant.id ? { ...t, lease_end: today } : t));
    addToast('Mietverhältnis beendet.', 'success');
  }

  function handleEdit(tenant) {
    setEditTenant(tenant);
    setEditForm({
      first_name: tenant.first_name || '', last_name: tenant.last_name || '',
      email: tenant.email || '', phone: tenant.phone || '',
      monthly_rent: tenant.monthly_rent || '', advance_payment: tenant.advance_payment || '',
      lease_start: tenant.lease_start || '', lease_end: tenant.lease_end || '',
      property_id: tenant.property_id || '',
    });
  }

  async function handleSaveEdit(e) {
    e.preventDefault(); setSaving(true);
    const { error } = await supabase.from('tenants').update({
      first_name: editForm.first_name, last_name: editForm.last_name,
      email: editForm.email || null, phone: editForm.phone || null,
      monthly_rent: editForm.monthly_rent !== '' ? Number(editForm.monthly_rent) : null,
      advance_payment: editForm.advance_payment !== '' ? Number(editForm.advance_payment) : null,
      lease_start: editForm.lease_start || null, lease_end: editForm.lease_end || null,
      property_id: editForm.property_id || null,
    }).eq('id', editTenant.id).eq('owner_id', user.id);
    setSaving(false);
    if (error) { addToast('Fehler: ' + error.message, 'error'); return; }
    setEditTenant(null);
    load();
    addToast('Mieter aktualisiert.', 'success');
  }

  async function handleDelete(tenant) {
    if (!window.confirm(`${tenant.first_name} ${tenant.last_name} wirklich löschen? Alle zugehörigen Daten gehen verloren.`)) return;
    const { error } = await supabase.from('tenants').delete().eq('id', tenant.id).eq('owner_id', user.id);
    if (error) { addToast('Fehler: ' + error.message, 'error'); return; }
    setTenants(prev => prev.filter(t => t.id !== tenant.id));
    addToast('Mieter gelöscht.', 'success');
  }

  /* derive filtered list */
  const filtered = tenants.filter(t => {
    /* search */
    if (search) {
      const q = search.toLowerCase();
      const name = `${t.first_name || ''} ${t.last_name || ''}`.toLowerCase();
      const prop = (t.properties?.name || '').toLowerCase();
      if (!name.includes(q) && !prop.includes(q) && !(t.email || '').toLowerCase().includes(q)) return false;
    }
    /* property */
    if (filterProp && t.property_id !== filterProp) return false;
    /* status */
    if (filterStatus) {
      const ls = leaseStatus(t.lease_start, t.lease_end);
      if (!ls || ls.label !== filterStatus) return false;
    }
    /* time range: tenant active during selected period */
    if (!customRange) {
      const start = new Date(`${yearFrom}-01-01`);
      const end   = new Date(`${yearTo}-12-31`);
      if (t.lease_start) {
        const ls = new Date(t.lease_start);
        if (ls > end) return false;
      }
      if (t.lease_end) {
        const le = new Date(t.lease_end);
        if (le < start) return false;
      }
    } else {
      if (customStart && customEnd) {
        const start = new Date(customStart);
        const end   = new Date(customEnd);
        if (t.lease_start && new Date(t.lease_start) > end) return false;
        if (t.lease_end   && new Date(t.lease_end)   < start) return false;
      }
    }
    return true;
  });

  const hasFilters = !!search || !!filterStatus || !!filterProp;

  function resetFilters() {
    setSearch('');
    setFilterStatus('');
    setFilterProp('');
    setYearFrom(currentYear());
    setYearTo(currentYear());
    setCustomRange(false);
    setCustomStart('');
    setCustomEnd('');
  }

  /* export CSV */
  function exportCsv() {
    const rows = [['Mieter', 'Email', 'Objekt', 'Mietbeginn', 'Mietende', 'Kaltmiete', 'NK-Vorauszahlung', 'Gesamtmiete', 'Status']];
    filtered.forEach(t => {
      const ls = leaseStatus(t.lease_start, t.lease_end);
      rows.push([
        `${t.first_name || ''} ${t.last_name || ''}`.trim(),
        t.email || '',
        t.properties?.name || '',
        fmt(t.lease_start),
        fmt(t.lease_end),
        fmtEur(t.monthly_rent),
        fmtEur(t.advance_payment),
        fmtEur((Number(t.monthly_rent)||0)+(Number(t.advance_payment)||0)),
        ls?.label || '',
      ]);
    });
    const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g,'""')}"`).join(',')).join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob(['\uFEFF'+csv], { type: 'text/csv;charset=utf-8' }));
    a.download = `Mietübersicht_${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
  }

  /* time range label */
  const timeLabel = customRange && customStart && customEnd
    ? `${fmtShort(customStart)} – ${fmtShort(customEnd)}`
    : yearFrom === yearTo
      ? `01.01.${String(yearFrom).slice(2)} – 31.12.${String(yearTo).slice(2)}`
      : `01.01.${String(yearFrom).slice(2)} – 31.12.${String(yearTo).slice(2)}`;

  /* summary totals */
  const totalKalt  = filtered.reduce((s, t) => s + (Number(t.monthly_rent)    || 0), 0);
  const totalNK    = filtered.reduce((s, t) => s + (Number(t.advance_payment) || 0), 0);
  const totalGes   = totalKalt + totalNK;

  const YEAR_RANGE = [0,1,2,3].map(i => currentYear() - i);

  return (
    <div style={{ padding: isMobile ? '16px' : '28px 32px', fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", maxWidth: 1300 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: isMobile ? 'center' : 'flex-start', justifyContent: 'space-between', marginBottom: 24, gap: 12 }}>
        <h1 style={{ fontSize: isMobile ? 22 : 28, fontWeight: 800, color: T.ink, margin: 0 }}>Mietübersicht</h1>
        <button
          onClick={exportCsv}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 7,
            padding: '8px 16px', borderRadius: 8, border: `1px solid ${T.border}`,
            background: 'white', color: T.ink, fontSize: 14, fontWeight: 500, cursor: 'pointer',
          }}
          onMouseEnter={e => e.currentTarget.style.background = T.bg}
          onMouseLeave={e => e.currentTarget.style.background = 'white'}
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
            <polyline points="7 10 12 15 17 10"/>
            <line x1="12" y1="15" x2="12" y2="3"/>
          </svg>
          {!isMobile && 'Exportieren'}
        </button>
      </div>

      {/* Tab bar */}
      <div style={{ borderBottom: `2px solid ${T.border}`, marginBottom: 24 }}>
        <div style={{
          display: 'inline-block', paddingBottom: 12, paddingRight: 4,
          borderBottom: `2px solid ${T.ink}`, marginBottom: -2,
          fontSize: 14, fontWeight: 700, color: T.ink, cursor: 'default',
        }}>
          Mietübersicht
        </div>
      </div>

      {/* Filter bar */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginBottom: 8 }}
        onMouseDown={e => e.stopPropagation()}>
        {/* Search */}
        <div style={{ position: 'relative' }}>
          <svg style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}
            width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.muted} strokeWidth="2" strokeLinecap="round">
            <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Miete suchen"
            style={{
              paddingLeft: 32, paddingRight: 12, paddingTop: 7, paddingBottom: 7,
              borderRadius: 6, border: `1px solid ${T.border}`, fontSize: 13,
              outline: 'none', width: 200, color: T.ink, background: 'white',
            }}
          />
        </div>

        {/* Filter button (opens filter dropdown) */}
        <Dropdown
          label={<><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="4" y1="6" x2="20" y2="6"/><line x1="8" y1="12" x2="16" y2="12"/><line x1="12" y1="18" x2="12" y2="18"/></svg> Filter</>}
          open={openFilter}
          onToggle={() => { setOpenFilter(o => !o); setOpenStatus(false); setOpenProp(false); setOpenTime(false); }}
          width={180}
        >
          <div style={{ padding: '4px 0' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: T.muted, padding: '4px 12px 8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Filter</div>
            <DropdownItem label="Status" selected={false} onClick={() => { setOpenFilter(false); setOpenStatus(true); }} />
            <DropdownItem label="Objekt" selected={false} onClick={() => { setOpenFilter(false); setOpenProp(true); }} />
            <DropdownItem label="Zeitraum" selected={false} onClick={() => { setOpenFilter(false); setOpenTime(true); }} />
          </div>
        </Dropdown>

        {/* Eigentümer chip — always shown, no action (single-user app) */}
        {!isMobile && (
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '5px 12px', borderRadius: 6, border: `1px solid ${T.border}`,
            background: 'white', fontSize: 13, color: T.ink, whiteSpace: 'nowrap',
          }}>
            Eigentümer
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={T.muted} strokeWidth="2.5" strokeLinecap="round">
              <polyline points="6 9 12 15 18 9"/>
            </svg>
          </div>
        )}

        {/* Zeitraum dropdown */}
        <Dropdown
          label={`Zeitraum: ${timeLabel}`}
          open={openTime}
          onToggle={() => { setOpenTime(o => !o); setOpenStatus(false); setOpenProp(false); setOpenFilter(false); }}
          width={260}
        >
          <div style={{ padding: '4px 0' }}
            onMouseDown={e => e.stopPropagation()}>
            <div style={{ fontSize: 11, fontWeight: 700, color: T.muted, padding: '4px 12px 8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Zeitraum</div>
            {YEAR_RANGE.map(y => (
              <DropdownItem
                key={y}
                label={`01.01.${String(y).slice(2)} – 31.12.${String(y).slice(2)}`}
                selected={!customRange && yearFrom === y && yearTo === y}
                onClick={() => { setYearFrom(y); setYearTo(y); setCustomRange(false); setOpenTime(false); }}
              />
            ))}
            <div style={{ borderTop: `1px solid ${T.border}`, margin: '8px 0 4px' }}/>
            <DropdownItem
              label="Individueller Zeitraum"
              selected={customRange}
              onClick={() => setCustomRange(true)}
            />
            {customRange && (
              <div style={{ padding: '8px 12px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ display: 'flex', gap: 8 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 11, color: T.muted, marginBottom: 4 }}>Von</div>
                    <input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)}
                      style={{ width: '100%', padding: '5px 8px', borderRadius: 6, border: `1px solid ${T.border}`, fontSize: 12, outline: 'none' }} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 11, color: T.muted, marginBottom: 4 }}>Bis</div>
                    <input type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)}
                      style={{ width: '100%', padding: '5px 8px', borderRadius: 6, border: `1px solid ${T.border}`, fontSize: 12, outline: 'none' }} />
                  </div>
                </div>
                <button
                  onClick={() => setOpenTime(false)}
                  style={{ padding: '6px 12px', borderRadius: 6, border: 'none', background: T.ink, color: 'white', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                >
                  Übernehmen
                </button>
              </div>
            )}
          </div>
        </Dropdown>

        {/* Status dropdown */}
        <Dropdown
          label="Status"
          open={openStatus}
          onToggle={() => { setOpenStatus(o => !o); setOpenProp(false); setOpenTime(false); setOpenFilter(false); }}
          width={180}
        >
          <div style={{ padding: '4px 0' }}>
            <DropdownItem label="Alle" selected={filterStatus === ''} onClick={() => { setFilterStatus(''); setOpenStatus(false); }} />
            {['Aktiv','Zukünftig','Beendet'].map(s => (
              <DropdownItem key={s} label={s} selected={filterStatus === s} onClick={() => { setFilterStatus(s); setOpenStatus(false); }} />
            ))}
          </div>
        </Dropdown>

        {/* Alle Objekte dropdown */}
        <Dropdown
          label={filterProp ? (properties.find(p => p.id === filterProp)?.name || 'Objekt') : 'Alle Objekte'}
          open={openProp}
          onToggle={() => { setOpenProp(o => !o); setOpenStatus(false); setOpenTime(false); setOpenFilter(false); }}
          width={240}
        >
          <div style={{ padding: '4px 0', maxHeight: 260, overflowY: 'auto' }}>
            <DropdownItem label="Alle Objekte" selected={filterProp === ''} onClick={() => { setFilterProp(''); setOpenProp(false); }} />
            {properties.map(p => (
              <DropdownItem key={p.id} label={p.name} selected={filterProp === p.id} onClick={() => { setFilterProp(p.id); setOpenProp(false); }} />
            ))}
          </div>
        </Dropdown>
      </div>

      {/* Active filter chips row */}
      {hasFilters && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8, marginTop: 4 }}>
          {filterStatus && <FilterChip label="Status" value={filterStatus} onRemove={() => setFilterStatus('')} />}
          {filterProp   && <FilterChip label="Objekt" value={properties.find(p => p.id === filterProp)?.name || ''} onRemove={() => setFilterProp('')} />}
          {search       && <FilterChip label="Suche" value={search} onRemove={() => setSearch('')} />}
        </div>
      )}

      {/* Filter zurücksetzen link */}
      {hasFilters && (
        <button
          onClick={resetFilters}
          style={{ background: 'none', border: 'none', color: T.ink, fontSize: 13, cursor: 'pointer', padding: '2px 0', marginBottom: 16 }}
        >
          Filter zurücksetzen
        </button>
      )}
      {!hasFilters && <div style={{ marginBottom: 16 }} />}

      {/* Summary bar (only when there are results) */}
      {!loading && filtered.length > 0 && (
        <div style={{
          display: isMobile ? 'grid' : 'flex',
          gridTemplateColumns: isMobile ? '1fr 1fr' : undefined,
          gap: isMobile ? 12 : 24,
          padding: isMobile ? '14px 16px' : '12px 20px', marginBottom: 16,
          background: 'white', borderRadius: 8, border: `1px solid ${T.border}`,
          flexWrap: 'wrap',
        }}>
          <div>
            <div style={{ fontSize: 11, color: T.muted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>Mietverhältnisse</div>
            <div style={{ fontSize: isMobile ? 16 : 18, fontWeight: 700, color: T.ink }}>{filtered.length}</div>
          </div>
          {!isMobile && <div style={{ width: 1, background: T.border }} />}
          <div>
            <div style={{ fontSize: 11, color: T.muted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>Kaltmiete/Monat</div>
            <div style={{ fontSize: isMobile ? 16 : 18, fontWeight: 700, color: T.ink }}>{fmtEur(totalKalt)}</div>
          </div>
          {!isMobile && <div style={{ width: 1, background: T.border }} />}
          <div>
            <div style={{ fontSize: 11, color: T.muted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>NK-Voraus./Monat</div>
            <div style={{ fontSize: isMobile ? 16 : 18, fontWeight: 700, color: T.ink }}>{fmtEur(totalNK)}</div>
          </div>
          {!isMobile && <div style={{ width: 1, background: T.border }} />}
          <div>
            <div style={{ fontSize: 11, color: T.muted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>Gesamt/Monat</div>
            <div style={{ fontSize: isMobile ? 16 : 18, fontWeight: 700, color: T.ink }}>{fmtEur(totalGes)}</div>
          </div>
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div style={{ background: 'white', borderRadius: 10, border: `1px solid ${T.border}`, overflow: 'hidden' }}>
          {[...Array(5)].map((_, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '1.6fr 1.4fr 1.1fr 110px 110px 110px 130px', gap: 16, padding: '16px 20px', borderBottom: i < 4 ? `1px solid ${T.border}` : 'none' }}>
              {[...Array(7)].map((_, j) => (
                <div key={j} style={{ height: 14, borderRadius: 6, background: '#f3f4f6', width: j === 0 ? '80%' : j === 6 ? 60 : '70%' }} />
              ))}
            </div>
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState filtered={hasFilters} onReset={resetFilters} />
      ) : (
        <>
          {/* Desktop table */}
          {!isMobile && (
            <div style={{ background: 'white', borderRadius: 10, border: `1px solid ${T.border}`, overflow: 'hidden' }}>
              {/* Table header */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1.6fr 1.4fr 1.1fr 110px 110px 110px 100px auto',
                padding: '10px 20px', background: T.bg,
                borderBottom: `1px solid ${T.border}`,
              }}>
                {['Mieter', 'Objekt', 'Mietzeitraum', 'Kaltmiete', 'NK-Voraus.', 'Gesamtmiete', 'Status', ''].map((h, i) => (
                  <div key={h || 'act'} style={{ fontSize: 11, fontWeight: 700, color: T.muted, textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: i >= 3 && i <= 5 ? 'right' : 'left', paddingRight: i >= 3 && i <= 5 ? 8 : 0 }}>
                    {h}
                  </div>
                ))}
              </div>
              {filtered.map((t, i) => (
                <Row key={t.id} tenant={t} last={i === filtered.length - 1} onEnd={handleEnd} onEdit={handleEdit} onDelete={handleDelete} />
              ))}
            </div>
          )}
          {/* Mobile card list */}
          {isMobile && (
            <div>
              {filtered.map((t, i) => (
                <Row key={t.id} tenant={t} last={i === filtered.length - 1} onEnd={handleEnd} onEdit={handleEdit} onDelete={handleDelete} isMobile />
              ))}
            </div>
          )}
        </>
      )}

      {/* Edit Modal */}
      {editTenant && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 24 }}
          onClick={e => { if (e.target === e.currentTarget) setEditTenant(null); }}>
          <div className="immo-modal" style={{ background: 'white', width: '100%', maxWidth: 520, maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ padding: '24px 28px 18px', borderBottom: `1px solid ${T.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800 }}>Mieter bearbeiten</h2>
              <button onClick={() => setEditTenant(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 22, color: T.muted, padding: 4 }}>×</button>
            </div>
            <form onSubmit={handleSaveEdit} style={{ padding: '22px 28px 28px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 14, marginBottom: 14 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: T.muted, marginBottom: 6 }}>Vorname</label>
                  <input value={editForm.first_name} onChange={e => setEditForm({ ...editForm, first_name: e.target.value })} required style={{ width: '100%', padding: '10px 12px', border: `1px solid ${T.border}`, borderRadius: 8, fontSize: 13, boxSizing: 'border-box' }} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: T.muted, marginBottom: 6 }}>Nachname</label>
                  <input value={editForm.last_name} onChange={e => setEditForm({ ...editForm, last_name: e.target.value })} required style={{ width: '100%', padding: '10px 12px', border: `1px solid ${T.border}`, borderRadius: 8, fontSize: 13, boxSizing: 'border-box' }} />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 14, marginBottom: 14 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: T.muted, marginBottom: 6 }}>E-Mail</label>
                  <input value={editForm.email} onChange={e => setEditForm({ ...editForm, email: e.target.value })} style={{ width: '100%', padding: '10px 12px', border: `1px solid ${T.border}`, borderRadius: 8, fontSize: 13, boxSizing: 'border-box' }} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: T.muted, marginBottom: 6 }}>Telefon</label>
                  <input value={editForm.phone} onChange={e => setEditForm({ ...editForm, phone: e.target.value })} style={{ width: '100%', padding: '10px 12px', border: `1px solid ${T.border}`, borderRadius: 8, fontSize: 13, boxSizing: 'border-box' }} />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 14, marginBottom: 14 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: T.muted, marginBottom: 6 }}>Kaltmiete (€)</label>
                  <input type="number" min="0" step="0.01" value={editForm.monthly_rent} onChange={e => setEditForm({ ...editForm, monthly_rent: e.target.value })} style={{ width: '100%', padding: '10px 12px', border: `1px solid ${T.border}`, borderRadius: 8, fontSize: 13, boxSizing: 'border-box' }} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: T.muted, marginBottom: 6 }}>NK-Vorauszahlung (€)</label>
                  <input type="number" min="0" step="0.01" value={editForm.advance_payment} onChange={e => setEditForm({ ...editForm, advance_payment: e.target.value })} style={{ width: '100%', padding: '10px 12px', border: `1px solid ${T.border}`, borderRadius: 8, fontSize: 13, boxSizing: 'border-box' }} />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 14, marginBottom: 14 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: T.muted, marginBottom: 6 }}>Mietbeginn</label>
                  <input type="date" value={editForm.lease_start} onChange={e => setEditForm({ ...editForm, lease_start: e.target.value })} style={{ width: '100%', padding: '10px 12px', border: `1px solid ${T.border}`, borderRadius: 8, fontSize: 13, boxSizing: 'border-box' }} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: T.muted, marginBottom: 6 }}>Mietende</label>
                  <input type="date" value={editForm.lease_end} onChange={e => setEditForm({ ...editForm, lease_end: e.target.value })} style={{ width: '100%', padding: '10px 12px', border: `1px solid ${T.border}`, borderRadius: 8, fontSize: 13, boxSizing: 'border-box' }} />
                </div>
              </div>
              <div style={{ marginBottom: 18 }}>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: T.muted, marginBottom: 6 }}>Objekt</label>
                <select value={editForm.property_id} onChange={e => setEditForm({ ...editForm, property_id: e.target.value })} style={{ width: '100%', padding: '10px 12px', border: `1px solid ${T.border}`, borderRadius: 8, fontSize: 13, boxSizing: 'border-box', cursor: 'pointer' }}>
                  <option value="">– Kein Objekt –</option>
                  {properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button type="submit" disabled={saving} className="btn-primary" style={{ flex: 1, height: 44, fontSize: 14, opacity: saving ? 0.6 : 1 }}>
                  {saving ? 'Speichert…' : 'Speichern'}
                </button>
                <button type="button" onClick={() => setEditTenant(null)} className="btn-secondary" style={{ height: 44, fontSize: 14, padding: '0 20px' }}>
                  Abbrechen
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
